import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type {
  AdminApp,
  AdminUserDetail,
  AdminUserProductAssignment,
  AdminUserSubscription,
} from '../types';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../lib/api';
import AdminAccessExpiry from '../components/admin/AdminAccessExpiry';

const { Title, Text } = Typography;

function toDate(value?: string | null) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDate(value?: string | null) {
  const date = toDate(value);
  return date ? date.toLocaleDateString() : '-';
}

function getRemainingDaysText(value?: string | null) {
  const date = toDate(value);
  if (!date) return '-';
  const msPerDay = 24 * 60 * 60 * 1000;
  const remaining = Math.ceil((date.getTime() - Date.now()) / msPerDay);
  if (remaining < 0) return 'Ended';
  if (remaining === 0) return 'Ends today';
  if (remaining === 1) return '1 day left';
  return `${remaining} days left`;
}

function getAssignedTrialDaysText(start?: string | null, end?: string | null) {
  const startDate = toDate(start);
  const endDate = toDate(end);
  if (!startDate || !endDate) return '-';
  const msPerDay = 24 * 60 * 60 * 1000;
  const total = Math.ceil((endDate.getTime() - startDate.getTime()) / msPerDay);
  if (total <= 0) return '-';
  return `${total} days`;
}

export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apps, setApps] = useState<AdminApp[]>([]);
  const [stripeLinkMissingIds, setStripeLinkMissingIds] = useState<Set<string>>(new Set());
  const [defaultTrialDays, setDefaultTrialDays] = useState<number | undefined>();
  const [subscriptionForm] = Form.useForm();

  useEffect(() => {
    if (!id) return;
    loadUser();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    loadApps();
    loadAdminConfig();
  }, [id]);

  useEffect(() => {
    if (!subscriptionForm || defaultTrialDays === undefined) return;
    const current = subscriptionForm.getFieldValue('trialDays');
    if (current === undefined) {
      subscriptionForm.setFieldsValue({ trialDays: defaultTrialDays });
    }
  }, [defaultTrialDays, subscriptionForm]);

  const organizationNames = useMemo(() => {
    return (
      user?.organizationMembers?.map((member) => member.organization?.name).filter(Boolean) || []
    );
  }, [user]);

  const loadUser = async () => {
    try {
      setLoading(true);
      const data = await api.admin.getUser(id!);
      setUser(data.user);
    } catch (error: any) {
      message.error(error.message || 'Failed to load user');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const loadApps = async () => {
    try {
      const data = await api.admin.listApps();
      const appList: AdminApp[] = (data.apps || []).filter(
        (app: AdminApp) => !app.isCatalogGroup && (!app.parentId || app.parent?.isCatalogGroup) && app.isActive !== false
      );
      setApps(appList);
    } catch (error: any) {
      message.error(error.message || 'Failed to load apps');
    }
  };

  const loadAdminConfig = async () => {
    try {
      const data = await api.admin.getConfig();
      if (typeof data?.stripeTrialDays === 'number') {
        setDefaultTrialDays(data.stripeTrialDays);
        subscriptionForm.setFieldsValue({ trialDays: data.stripeTrialDays });
      }
    } catch {
      // ignore
    }
  };

  const handleCreateSubscription = async (values: {
    appId: string;
    priceId?: string;
    trialDays?: number;
    trialOnly?: boolean;
    manual?: boolean;
    expiresAt?: string;
    grantType?: 'STANDARD' | 'TRIAL' | 'MANUAL';
  }) => {
    if (!id) return;
    try {
      setSaving(true);
      await api.admin.createUserSubscription(id, { ...values, manual: values.grantType === 'MANUAL', trialOnly: values.grantType === 'TRIAL', expiresAt: values.grantType === 'MANUAL' && values.expiresAt ? new Date(values.expiresAt).toISOString() : undefined });
      message.success('Subscription added');
      subscriptionForm.resetFields();
      if (defaultTrialDays !== undefined) {
        subscriptionForm.setFieldsValue({ trialDays: defaultTrialDays });
      }
      await loadUser();
    } catch (error: any) {
      message.error(error.message || 'Failed to add subscription');
    } finally {
      setSaving(false);
    }
  };

  const isTrialSubscription = (subscription: AdminUserSubscription) =>
    subscription.status === 'TRIAL' ||
    Boolean(subscription.trialStart) ||
    Boolean(subscription.trialEnd);

  const handleCancelTrial = async (subscriptionId: string, allowRetrial: boolean) => {
    if (!id) return;
    try {
      setSaving(true);
      const result = await api.admin.cancelUserTrial(id, subscriptionId, allowRetrial);
      if (result?.stripeSubscriptionMissing) {
        setStripeLinkMissingIds((prev) => new Set(prev).add(subscriptionId));
        message.warning('Stripe link missing. Trial was canceled locally.');
      }
      message.success(
        allowRetrial
          ? 'Trial canceled and history reset.'
          : 'Trial canceled.'
      );
      await loadUser();
    } catch (error: any) {
      message.error(error.message || 'Failed to cancel trial');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCanceledTrial = async (subscriptionId: string) => {
    if (!id) return;
    try {
      setSaving(true);
      await api.admin.deleteUserCanceledTrial(id, subscriptionId);
      message.success('Canceled trial deleted. User can start a new trial.');
      await loadUser();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete canceled trial');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTrialHistory = async (appId: string) => {
    if (!id) return;
    try {
      setSaving(true);
      await api.admin.deleteUserTrialHistory(id, appId);
      message.success('Trial history reset. User can start a new trial.');
      await loadUser();
    } catch (error: any) {
      message.error(error.message || 'Failed to reset trial history');
    } finally {
      setSaving(false);
    }
  };

  const subscriptionColumns: ColumnsType<AdminUserSubscription> = [
    {
      title: 'App',
      key: 'app',
      render: (_value, record) => record.app?.name || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value, record) => (
        <Space size={4} wrap>
          <Tag color={value === 'ACTIVE' ? 'green' : value === 'TRIAL' ? 'blue' : 'default'}>
            {value}
          </Tag>
          {value === 'TRIAL' && !record.stripeSubscriptionId && !record.price && (
            <Tag color="cyan">TRIAL-ONLY</Tag>
          )}
          {stripeLinkMissingIds.has(record.id) && (
            <Tag color="warning">Stripe link missing</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Price',
      key: 'price',
      render: (_value, record) =>
        record.price?.name || record.price?.interval || '-',
    },
    {
      title: 'Period End',
      key: 'periodEnd',
      render: (_value, record) => formatDate(record.trialEnd || record.currentPeriodEnd),
    },
    {
      title: 'Remaining',
      key: 'remaining',
      render: (_value, record) => getRemainingDaysText(record.trialEnd || record.currentPeriodEnd),
    },
    {
      title: 'Trial Length',
      key: 'trialLength',
      render: (_value, record) =>
        record.status === 'TRIAL'
          ? getAssignedTrialDaysText(record.trialStart || record.currentPeriodStart, record.trialEnd || record.currentPeriodEnd)
          : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_value, record) => {
        const isTrial = isTrialSubscription(record);
        const isStripeManaged = Boolean(record.stripeSubscriptionId);
        const expiryButton = !isStripeManaged && ['ACTIVE', 'TRIAL'].includes(record.status) ? <AdminAccessExpiry subscription={record} onSave={(end) => api.admin.updateManualAccessExpiry('users', id!, record.id, end)} onUpdated={loadUser} /> : null;
        if (!isTrial && !isStripeManaged && record.status === 'ACTIVE') {
          return <Space>{expiryButton}<Popconfirm title="Revoke manual access?" onConfirm={async () => {
            try { await api.admin.cancelUserSubscription(id!, record.id); message.success('Access revoked'); await loadUser(); }
            catch (error: any) { message.error(error.message || 'Failed to revoke access'); }
          }}><Button danger size="small">Revoke Access</Button></Popconfirm></Space>;
        }
        if (!isTrial) {
          return <Text type="secondary">—</Text>;
        }
        if (record.status === 'TRIAL') {
          return (
            <Space>
              {expiryButton}
              <Popconfirm
                title="Cancel trial?"
                onConfirm={() => handleCancelTrial(record.id, false)}
                okText="Cancel Trial"
                okButtonProps={{ danger: true }}
              >
                <Button danger size="small">Cancel Trial</Button>
              </Popconfirm>
              {!isStripeManaged && (
                <Popconfirm
                  title="Cancel trial and allow new trial again?"
                  description="Current trial record will be deleted."
                  onConfirm={() => handleCancelTrial(record.id, true)}
                  okText="Cancel + Reset"
                  okButtonProps={{ danger: true }}
                >
                  <Button size="small">Cancel + Reset</Button>
                </Popconfirm>
              )}
            </Space>
          );
        }
        if (record.status === 'CANCELED') {
          return (
            <Popconfirm
              title="Delete canceled trial?"
              description="User will be eligible for a new trial again."
              onConfirm={() => handleDeleteCanceledTrial(record.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button danger size="small">Delete Canceled Trial</Button>
            </Popconfirm>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
  ];

  const trialHistoryColumns: ColumnsType<NonNullable<AdminUserDetail['emailTrialHistory']>[number]> = [
    {
      title: 'App',
      key: 'app',
      render: (_value, record) => record.app?.name || '-',
    },
    {
      title: 'Recorded',
      key: 'recorded',
      render: (_value, record) =>
        record.createdAt ? new Date(record.createdAt).toLocaleDateString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_value, record) => (
        <Popconfirm
          title="Reset trial history for this app?"
          description="This will allow the user to start a trial again."
          onConfirm={() => handleDeleteTrialHistory(record.appId)}
          okText="Reset"
          okButtonProps={{ danger: true }}
        >
          <Button danger size="small">Reset Trial History</Button>
        </Popconfirm>
      ),
    },
  ];

  const organizationSeatColumns: ColumnsType<AdminUserProductAssignment> = [
    {
      title: 'App',
      key: 'app',
      render: (_value, record) => record.app?.name || '-',
    },
    {
      title: 'Organization',
      key: 'organization',
      render: (_value, record) => record.organization?.name || '-',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_value, record) => (
        <Tag color={record.subscription?.status === 'ACTIVE' ? 'green' : record.subscription?.status === 'TRIAL' ? 'blue' : 'default'}>
          {record.subscription?.status || '-'}
        </Tag>
      ),
    },
    {
      title: 'Price',
      key: 'price',
      render: (_value, record) =>
        record.subscription?.price?.name || record.subscription?.price?.interval || '-',
    },
    {
      title: 'Period End',
      key: 'periodEnd',
      render: (_value, record) => formatDate(record.subscription?.currentPeriodEnd),
    },
    {
      title: 'Assigned',
      key: 'assignedAt',
      render: (_value, record) => formatDate(record.assignedAt),
    },
  ];

  if (loading) {
    return <Card loading />;
  }

  if (!user) {
    return null;
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>{user.name || user.email}</Title>
          <Space wrap>
            <Text type="secondary">{user.email}</Text>
            <Tag color="blue">{user.stripeCustomerId ? 'Customer set' : 'No customer'}</Tag>
          </Space>
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            Created {new Date(user.createdAt).toLocaleDateString()}
          </Text>
        </div>
        <Space>
          <Popconfirm
            title="Delete user?"
            description="Only possible when there are no active subscriptions."
            onConfirm={async () => {
              try {
                await api.admin.deleteUser(user.id);
                message.success('User deleted');
                navigate('/admin/users');
              } catch (error: any) {
                message.error(error.message || 'Failed to delete user');
              }
            }}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button danger>Delete</Button>
          </Popconfirm>
          <Button onClick={() => navigate('/admin/users')}>Back</Button>
        </Space>
      </div>

      <Card title="Organization">
        <Text type="secondary">
          Assigned organizations:
        </Text>
        <div style={{ marginTop: 8 }}>
          {organizationNames.length > 0 ? (
            <Space wrap>
              {organizationNames.map((org) => (
                <Tag key={org} color="purple">
                  {org}
                </Tag>
              ))}
            </Space>
          ) : (
            <Text type="secondary">No organization</Text>
          )}
        </div>
      </Card>

      <Card title="Subscriptions">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Form
            form={subscriptionForm}
            layout="inline"
            onFinish={handleCreateSubscription}
            initialValues={{ trialDays: defaultTrialDays }}
          >
            <Form.Item
              name="appId"
              rules={[{ required: true, message: 'App is required' }]}
            >
              <Select
                placeholder="App"
                style={{ width: 220 }}
                options={apps.map((app) => ({ value: app.id, label: app.name }))}
                onChange={(appId) => subscriptionForm.setFieldsValue({ priceId: undefined, grantType: apps.find((app) => app.id === appId)?.isAdminGranted ? 'TRIAL' : 'STANDARD' })}
              />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prev, current) =>
                prev.appId !== current.appId || prev.grantType !== current.grantType
              }
            >
              {({ getFieldValue }) => {
                const appId = getFieldValue('appId') as string | undefined;
                const trialOnly = ['TRIAL', 'MANUAL'].includes(getFieldValue('grantType'));
                const priceOptions = (
                  apps.find((app) => app.id === appId)?.prices || []
                ).map((price) => ({
                  value: price.id,
                  label: `${price.name || price.interval} (${price.currency.toUpperCase()}${price.unitAmount ? ' ' + price.unitAmount / 100 : ''})`,
                }));
                return (
                  <Form.Item
                    name="priceId"
                    rules={trialOnly ? [] : [{ required: true, message: 'Price is required' }]}
                  >
                    <Select
                      placeholder={trialOnly ? 'Not required for local access' : 'Price'}
                      style={{ width: 220 }}
                      options={priceOptions}
                      disabled={trialOnly || priceOptions.length === 0}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.appId !== next.appId}>
              {({ getFieldValue }) => (
                <Form.Item name="grantType" initialValue="STANDARD">
                  <Select style={{ width: 220 }} options={[
                    { value: 'STANDARD', label: 'Standard subscription', disabled: Boolean(apps.find((app) => app.id === getFieldValue('appId'))?.isAdminGranted) },
                    { value: 'TRIAL', label: 'Trial only' },
                    { value: 'MANUAL', label: 'Manual subscription' },
                  ]} />
                </Form.Item>
              )}
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.grantType !== next.grantType}>
              {({ getFieldValue }) => getFieldValue('grantType') === 'MANUAL' ? (
                <Form.Item name="expiresAt" label="Access expiry (optional)">
                  <Input type="datetime-local" />
                </Form.Item>
              ) : null}
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, next) => prev.grantType !== next.grantType}>
              {({ getFieldValue }) => getFieldValue('grantType') !== 'MANUAL' ? (
                <Form.Item name="trialDays"><InputNumber min={1} placeholder="Trial days" /></Form.Item>
              ) : null}
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving}>
                Create Subscription
              </Button>
            </Form.Item>
          </Form>

          <Table
            rowKey="id"
            dataSource={user.userSubscriptions || []}
            columns={subscriptionColumns}
            pagination={false}
          />
        </Space>
      </Card>

      <Card title="Organization Seats">
        <Table
          rowKey="id"
          dataSource={user.productAssignments || []}
          columns={organizationSeatColumns}
          pagination={false}
          locale={{ emptyText: 'No organization seats assigned' }}
        />
      </Card>

      <Card title="Trial History">
        <Table
          rowKey="id"
          dataSource={user.emailTrialHistory || []}
          columns={trialHistoryColumns}
          pagination={false}
        />
      </Card>
    </Space>
  );
}
